import React, { forwardRef, useEffect, useState } from 'react'

import { CloseIconMedium } from '@hm-group/fabric-icons/icons/CloseIcon'
import cn from 'classnames'
import { Transition } from 'react-transition-group'

import IconButton from 'components/Buttons/IconButton'
import HeadingLarge from 'components/Typography/headings/HeadingLarge'
import FocusTrap from 'helpers/FocusTrap'
import Overlay from 'helpers/Overlay'

import { getScrollbarSize, getBodyPadding } from './handleScrollbar'
import styles from './Modal.module.scss'
import { ModalProps } from './types'

const Modal: React.FC<ModalProps> = forwardRef(
  (
    {
      actions,
      heading,
      closeLabel,
      content,
      onClose,
      isOpen,
      size = 'small',
      cueModal = undefined,
      testId,
    },
    ref,
  ) => {
    const [tabIndex, setTabIndex] = useState(-1)
    const ariaLabelId = 'modal-heading'

    useEffect(() => {
      if (isOpen) {
        setTabIndex(0)
        document.body.style.paddingInlineEnd = `${getBodyPadding() + getScrollbarSize()}px`
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'
      }
      return () => {
        setTabIndex(-1)
        document.body.style.overflow = ''
        document.documentElement.style.overflow = ''
        document.body.style.paddingInlineEnd = ''
      }
    }, [isOpen])

    return (
      <>
        <Transition
          in={cueModal ? cueModal.isModalVisible : isOpen}
          timeout={{
            appear: 0,
            enter: 0,
            exit: 300,
          }}
          mountOnEnter
          unmountOnExit
        >
          {(transitionState) => (
            <FocusTrap
              name={ariaLabelId}
              onEscape={onClose}
              className={cn(
                styles.modal,
                styles[`size_${size}`],
                transitionState && styles[`modal_${transitionState}`],
              )}
              aria-labelledby={ariaLabelId}
            >
              <div className={styles.header}>
                <HeadingLarge
                  Tag="h2"
                  isMarginless
                  id={ariaLabelId}
                  className={styles.header_heading}
                >
                  {heading}
                </HeadingLarge>
                <IconButton
                  className={styles.closeButton}
                  variant="ghost"
                  iconElement={<CloseIconMedium />}
                  onClick={onClose}
                  tabIndex={tabIndex}
                >
                  {closeLabel}
                </IconButton>
              </div>

              <div className={styles.content} data-testid={testId} ref={ref}>
                {content}
              </div>

              {actions && <div className={styles.actions}>{actions}</div>}
            </FocusTrap>
          )}
        </Transition>
        <div className={styles.overlay}>
          <Overlay isIn={cueModal ? cueModal.isOverlayVisible : isOpen} />
        </div>
      </>
    )
  },
)

export default Modal
